/**
 * localStorage shim that survives Safari Private Browsing and
 * QuotaExceededError. Falls back to an in-memory Map for the lifetime
 * of the page when persistent storage is unavailable.
 *
 * Use this in any code path where a thrown setItem would break the
 * app (token storage, theme persistence, language preference). Existing
 * `localStorage.*` direct calls can migrate over time.
 *
 * The shape mirrors the native Storage API so callers can swap
 * `localStorage.setItem(k, v)` for `safeStorage.setItem(k, v)` with no
 * other change.
 */

const memoryFallback = new Map<string, string>();
let useMemoryFallback = false;

function probeOnce(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    useMemoryFallback = true;
    return false;
  }
  try {
    const probeKey = '__safeStorage_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    useMemoryFallback = true;
    return false;
  }
}

probeOnce();

export const safeStorage = {
  getItem(key: string): string | null {
    if (useMemoryFallback) return memoryFallback.get(key) ?? null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      useMemoryFallback = true;
      return memoryFallback.get(key) ?? null;
    }
  },
  setItem(key: string, value: string): void {
    if (useMemoryFallback) {
      memoryFallback.set(key, value);
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      useMemoryFallback = true;
      memoryFallback.set(key, value);
    }
  },
  removeItem(key: string): void {
    memoryFallback.delete(key);
    if (useMemoryFallback) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      useMemoryFallback = true;
    }
  },
};
